import { FLOOR_EXTENT } from "@/lib/location-scene-3d/palette"
import { DEFAULT_CAMERA_STATE, type SceneCameraState } from "@/lib/location-scene-3d/scene-state"

/** Platform span the stock camera pose was framed for (32×32 m). */
export const CAMERA_REFERENCE_SPAN = 32

const CAMERA_POSE_POS_EPS = 0.05
const CAMERA_POSE_ANGLE_EPS = 0.01
/** Reframe when the platform span grows or shrinks by about a quarter. */
const SPAN_REFRAME_RATIO = 1.25

function positiveSpan(span: number, fallback: number): number {
  return Number.isFinite(span) && span > 0 ? span : fallback
}

/** Same yaw / pitch / fov as the 32 m pose; position and target scale with span. */
export function cameraPoseForSpan(span: number): SceneCameraState {
  const s = positiveSpan(span, CAMERA_REFERENCE_SPAN)
  const k = s / CAMERA_REFERENCE_SPAN
  const base = DEFAULT_CAMERA_STATE
  const target = base.target ?? [0, 0.5, 0]
  return {
    pos: [base.pos[0] * k, base.pos[1] * k, base.pos[2] * k],
    target: [target[0] * k, target[1] * k, target[2] * k],
    yaw: base.yaw,
    pitch: base.pitch,
    fov: base.fov,
    mode: base.mode,
  }
}

export function cameraPoseMatchesDefault(pose: { pos: [number, number, number]; yaw: number; pitch: number }): boolean {
  const base = DEFAULT_CAMERA_STATE
  for (let i = 0; i < 3; i++) {
    if (Math.abs(pose.pos[i] - base.pos[i]) > CAMERA_POSE_POS_EPS) return false
  }
  if (Math.abs(pose.yaw - base.yaw) > CAMERA_POSE_ANGLE_EPS) return false
  if (Math.abs(pose.pitch - base.pitch) > CAMERA_POSE_ANGLE_EPS) return false
  return true
}

export function spanShiftNeedsReframe(previousSpan: number, nextSpan: number): boolean {
  if (!(previousSpan > 0) || !(nextSpan > 0)) return false
  const ratio = nextSpan / previousSpan
  return ratio >= SPAN_REFRAME_RATIO || ratio <= 1 / SPAN_REFRAME_RATIO
}

/** Fly / light workspace half-extent (meters). No-platform floors stay at the old 32 m grid. */
export function sceneTravelBound(span: number): number {
  if (!(Number.isFinite(span) && span > 0)) return Math.max(FLOOR_EXTENT * 2, 50)
  return Math.max(span * 2, 0.25)
}

/** Perspective near plane. 32 m keeps 0.05; smaller sets can approach without clipping. */
export function cameraNearForSpan(span: number): number {
  if (!(Number.isFinite(span) && span > 0)) return 0.05
  return Math.min(0.05, Math.max(0.001, span * 0.002))
}

/** Lowest fly height. 32 m keeps 0.05; a centimeter set can sit closer to the floor. */
export function flyMinY(span: number): number {
  if (!(Number.isFinite(span) && span > 0)) return 0.05
  return Math.min(0.05, span * 0.02)
}

/** Light-gizmo drag in meters per pixel. At 32 m this is ~0.064, matching the old 0.06. */
export function lightDragMetersPerPx(span: number): number {
  return positiveSpan(span, CAMERA_REFERENCE_SPAN) * 0.002
}

/** Gap above the lamp sphere for the intensity slider. 32 m and larger keep 0.35 m. */
export function lightGizmoGap(span: number): number {
  const s = positiveSpan(span, CAMERA_REFERENCE_SPAN)
  return Math.min(0.35, (s * 0.35) / CAMERA_REFERENCE_SPAN)
}

function positiveSector(sectorM: number): number {
  return Number.isFinite(sectorM) && sectorM > 0 ? sectorM : 1
}

/** Meters on a 1 m cell; a fraction of the cell when the cell is smaller. */
function chromeGap(sectorM: number, meters: number, fraction: number): number {
  return Math.min(meters, positiveSector(sectorM) * fraction)
}

/** Orange floor-selection cap thickness. 2.5 cm on a 1 m cell. */
export function selectCapHeight(sectorM: number): number {
  return chromeGap(sectorM, 0.025, 0.08)
}

/** Gap under the selection cap so it clears the floor top. */
export function selectCapLift(sectorM: number): number {
  return chromeGap(sectorM, 0.002, 0.02)
}

/** Gap under a painted flat cap. */
export function selectCapSeat(sectorM: number): number {
  return chromeGap(sectorM, 0.001, 0.01)
}

/** Extra lift for the see-through paint plane above the selection cap. */
export function selectPaintLift(sectorM: number): number {
  return chromeGap(sectorM, 0.005, 0.04)
}

/** Sector grid height above y=0. */
export function sectorGridY(sectorM: number): number {
  return chromeGap(sectorM, 0.005, 0.04)
}

/** Wall-selection overlay gap along the outward normal. */
export function wallOverlayGap(sectorM: number): number {
  return chromeGap(sectorM, 0.005, 0.04)
}

/**
 * Toolbar sits this far above the selection.
 * 1 m cell keeps 0.15 m. The old 0.12 m floor is capped by the cell so a 1 cm cell gets ~8 mm.
 */
export function selectionToolbarPad(sectorM: number): number {
  const s = positiveSector(sectorM)
  return Math.max(s * 0.15, Math.min(0.12, s * 0.8))
}
